/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IViewRefItem, IViewRefResolver, TSchemaConstObjectProps } from "@adapptio/lib-blueprint";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { createEventEmitter, emitEvent, offEvent, onEvent } from "@adapptio/lib-shared";
import { ResourceService } from "../../resources/ResourceService";

/**
 * View Ref Resolver
 */
export class ViewRefResolver implements IViewRefResolver {
	/** Invalidate event */
	public onInvalidate = createEventEmitter<void>();

	private invalidateHandler: (resourceType: RESOURCE_TYPES) => void;

	/**
	 * Constructor
	 */
	public constructor(private resourceSvc: ResourceService) {
		this.invalidateHandler = (resourceType) => {
			if (resourceType === RESOURCE_TYPES.VIEW) {
				emitEvent(this.onInvalidate);
			}
		};

		onEvent(this.resourceSvc.onResourceTypeChange, this.invalidateHandler);
	}

	/**
	 * Returns a params schema by view ID
	 */
	public getParamsSchemaById(viewId: string): TSchemaConstObjectProps {
		const resource = this.resourceSvc.getResourceById(viewId);

		if (!resource || resource.resourceType !== RESOURCE_TYPES.VIEW) {
			return null;
		}

		return resource.paramsSchema;
	}

	/**
	 * Returns a list of available views
	 */
	public getViewList(): IViewRefItem[] {
		return this.resourceSvc.getResourceListByType(RESOURCE_TYPES.VIEW).map((resource) => ({
			viewId: resource.id,
			label: resource.descriptor.name,
			description: null
		}));
	}

	/**
	 * Disposes resolver
	 */
	public dispose(): void {
		offEvent(this.resourceSvc.onResourceTypeChange, this.invalidateHandler);
	}
}
